import { gql, useMutation } from '@apollo/client';
import React, { useState } from 'react';

import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { ConfirmationModal } from './confirmationModal/ConfirmationModal';
import { DefaultOverrideButton } from './defaultOverrideButton/DefaultOverrideButton';
import { UndoMarkAsIgnoredButton } from './undoMarkAsIgnoredButton/UndoMarkAsIgnoredButton';
import { UndoMarkAsSafeButton } from './undoMarkAsSafeButton/UndoMarkAsSafeButton';

export const overrideButtonAffectedQueryFragment = gql`
  fragment OverrideButtonAffectedQueryFragment on AffectedQuery {
    id
    alreadyApproved
    alreadyIgnored
    changes {
      impact
    }
  }
`;

export const markChangesAsSafeMutation = gql`
  mutation MarkChangesAsSafeMutation(
    $graphId: ID!
    $checkId: ID!
    $operationId: ID!
  ) {
    service(id: $graphId) {
      markChangesForOperationAsSafe(
        checkID: $checkId
        operationID: $operationId
      ) {
        success
        affectedOperation {
          id
          ...OverrideButtonAffectedQueryFragment
        }
      }
    }
  }
  ${overrideButtonAffectedQueryFragment}
`;

export const unmarkChangesAsSafeMutation = gql`
  mutation UnmarkChangesAsSafeMutation(
    $graphId: ID!
    $checkId: ID!
    $operationId: ID!
  ) {
    service(id: $graphId) {
      unmarkChangesForOperationAsSafe(
        checkID: $checkId
        operationID: $operationId
      ) {
        success
        affectedOperation {
          id
          ...OverrideButtonAffectedQueryFragment
        }
      }
    }
  }
  ${overrideButtonAffectedQueryFragment}
`;

export const ignoreOperationsForChecksMutation = gql`
  mutation IgnoreOperationsForChecksMutation(
    $graphId: ID!
    $checkId: ID!
    $operationIds: [ID!]!
  ) {
    service(id: $graphId) {
      ignoreOperationsInChecks(ids: $operationIds) {
        graph {
          id
          schemaTagById(id: $checkId) {
            id
            diffToPrevious {
              affectedQueries {
                id
                ...OverrideButtonAffectedQueryFragment
              }
            }
          }
        }
      }
    }
  }
  ${overrideButtonAffectedQueryFragment}
`;

export const unignoreOperationsForChecksMutation = gql`
  mutation UnignoreOperationsForChecksMutation(
    $graphId: ID!
    $checkId: ID!
    $operationIds: [ID!]!
  ) {
    service(id: $graphId) {
      unignoreOperationsInChecks(ids: $operationIds) {
        graph {
          id
          schemaTagById(id: $checkId) {
            id
            diffToPrevious {
              affectedQueries {
                id
                ...OverrideButtonAffectedQueryFragment
              }
            }
          }
        }
      }
    }
  }
  ${overrideButtonAffectedQueryFragment}
`;

type ConfirmationAction = 'markAsSafe' | 'unmarkAsSafe' | 'ignore' | 'unignore';

interface Props {
  graphId: string;
  checkId: string;
  operation: GraphQLTypes.OverrideButtonAffectedQueryFragment;
}
export const LegacyOverrideButton = ({
  graphId,
  checkId,
  operation,
}: Props) => {
  const [markChangesAsSafe] = useMutation<
    GraphQLTypes.MarkChangesAsSafeMutation,
    GraphQLTypes.MarkChangesAsSafeMutationVariables
  >(markChangesAsSafeMutation);

  const [unmarkChangesAsSafe] = useMutation<
    GraphQLTypes.UnmarkChangesAsSafeMutation,
    GraphQLTypes.UnmarkChangesAsSafeMutationVariables
  >(unmarkChangesAsSafeMutation);

  const [ignoreOperations] = useMutation<
    GraphQLTypes.IgnoreOperationsForChecksMutation,
    GraphQLTypes.IgnoreOperationsForChecksMutationVariables
  >(ignoreOperationsForChecksMutation);

  const [unignoreOperations] = useMutation<
    GraphQLTypes.UnignoreOperationsForChecksMutation,
    GraphQLTypes.UnignoreOperationsForChecksMutationVariables
  >(unignoreOperationsForChecksMutation);

  const [confirmAction, setConfirmAction] = useState<ConfirmationAction | null>(
    null,
  );

  const exactlyOneChange = operation?.changes?.length === 1;

  return (
    <>
      {operation.alreadyApproved ? (
        <UndoMarkAsSafeButton
          graphId={graphId}
          onUndoClick={() => setConfirmAction('unmarkAsSafe')}
          exactlyOneChange={exactlyOneChange}
        />
      ) : operation.alreadyIgnored ? (
        <UndoMarkAsIgnoredButton
          graphId={graphId}
          onUndoClick={() => setConfirmAction('unignore')}
        />
      ) : (
        <DefaultOverrideButton
          className="my-1"
          graphId={graphId}
          onMarkSafeClick={() => setConfirmAction('markAsSafe')}
          onIgnoreClick={() => setConfirmAction('ignore')}
          exactlyOneChange={operation.changes?.length === 1}
        />
      )}
      <ConfirmationModal
        isOpen={confirmAction === 'markAsSafe'}
        title={`Mark change${exactlyOneChange ? '' : 's'} as safe?`}
        description={
          <>
            By selecting this action, you acknowledge that the change
            {exactlyOneChange ? '' : 's'} for this operation will not affect the
            clients that execute it.
          </>
        }
        onSubmit={() =>
          markChangesAsSafe({
            variables: {
              graphId,
              checkId,
              operationId: operation.id,
            },
          })
        }
        onClose={() => setConfirmAction(null)}
      />
      <ConfirmationModal
        isOpen={confirmAction === 'unmarkAsSafe'}
        title={`Unmark change${exactlyOneChange ? '' : 's'} as safe?`}
        description={
          <>
            By reverting the approval for this operation, you acknowledge that{' '}
            {exactlyOneChange ? 'this change' : 'these changes'} may be marked
            as breaking this operation in future checks.
          </>
        }
        onSubmit={() =>
          unmarkChangesAsSafe({
            variables: {
              graphId,
              checkId,
              operationId: operation.id,
            },
          })
        }
        onClose={() => setConfirmAction(null)}
      />
      <ConfirmationModal
        isOpen={confirmAction === 'ignore'}
        title="Ignore this operation?"
        description={
          <>
            By selecting this action, you acknowledge that this specific
            operation is no longer in use. Changes affecting it will continue to
            be tracked, but won't affect the outcome of future checks.
          </>
        }
        onSubmit={() =>
          ignoreOperations({
            variables: {
              graphId,
              checkId,
              operationIds: [operation.id],
            },
          })
        }
        onClose={() => setConfirmAction(null)}
      />
      <ConfirmationModal
        isOpen={confirmAction === 'unignore'}
        title="No longer ignore this operation?"
        description={
          <>
            By no longer ignoring this operation, you acknowledge that changes
            affecting this operation should affect the outcome of future checks.
          </>
        }
        onSubmit={() =>
          unignoreOperations({
            variables: {
              graphId,
              checkId,
              operationIds: [operation.id],
            },
          })
        }
        onClose={() => setConfirmAction(null)}
      />
    </>
  );
};
